<script setup lang="ts">
import WrDesignHeroCard from "~/components/molecules/WrDesignHeroCard.vue";
import WrDesignShineText from "~/components/molecules/WrDesignShineText.vue";
import WrDesignProcessCardsGrid from "~/components/molecules/WrDesignProcessCardsGrid.vue";
import WrDesignReadyToElevateYourBrand from "~/components/molecules/WrDesignReadyToElevateYourBrand.vue";
import WrProblemSolutionSection from "~/components/molecules/WrProblemSolutionSection.vue";
import WrDesignServiceCardsSection from "~/components/molecules/WrDesignServiceCardsSection.vue";
import WrDesignBenefitCards from "~/components/molecules/WrDesignBenefitCards.vue";
import WrDesignIconLineDecorativeSquaresCards from "~/components/molecules/WrDesignIconLineDecorativeSquaresCards.vue";
import WrDesignDualCardsSection from "~/components/molecules/WrDesignDualCardsSection.vue";
import WrDesignRealWorldApplicationsSection from "~/components/molecules/WrDesignRealWorldApplicationsSection.vue";
import WrDesignFourCardTransformGridSection from "~/components/molecules/WrDesignFourCardTransformGridSection.vue";
import WrDesignQuoteCtaSection from "~/components/molecules/WrDesignQuoteCtaSection.vue";
import WrDesignFaqsSection from "~/components/molecules/WrDesignFaqsSection.vue";
import WrDesignStatsCard from "~/components/molecules/WrDesignStatsCard.vue";
import WrIcon from "~/components/global/WrIcon.vue";

const WrContactFormPopup = defineAsyncComponent(
  () => import("~/components/molecules/WrContactFormPopup.vue")
);

/* ─── State & Composables ─── */
const visible = ref<boolean>(false);
const openModal = (): void => {
  visible.value = true;
};

const colorMode = useColorMode();
const isLight = computed<boolean>(() => colorMode.value !== "dark");

/* ─── Fetch Content ─── */
const { data } = await useFetch(
  `${useRequestURL().origin}/content/services/cms-development/ghost-development-services-content.json`,
);
if (!data.value) throw createError({ statusCode: 404, statusMessage: "Page content not found" });
const content = data.value as any;

const {
  meta_tags,
  hero,
  shineText,
  quickHits,
  businessOutcomes,
  whyItMatters,
  capabilities,
  struggles,
  techStack,
  integrationCards,
  dualCards,
  whyChooseUs,
  cta,
  faq,
} = content;

/* ─── SEO Metadata & Schema ─── */
useHead({
  title: meta_tags.title,
  link: [{ rel: "canonical", href: meta_tags.canonical }],
  meta: [...meta_tags.meta, { name: "robots", content: "index, follow" }],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: meta_tags.serviceSchema.name,
        serviceType: meta_tags.serviceSchema.serviceType,
        url: meta_tags.canonical,
        provider: {
          "@type": "Organization",
          name: "WebReinvent",
          url: "https://webreinvent.com",
        },
        description: meta_tags.serviceSchema.description,
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://webreinvent.com" },
          { "@type": "ListItem", position: 2, name: "Services", item: "https://webreinvent.com/services" },
          { "@type": "ListItem", position: 3, name: meta_tags.serviceSchema.breadcrumbName, item: meta_tags.canonical },
        ],
      }),
    },
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.faqItems.map((item: { title: string; description: string }) => ({
          "@type": "Question",
          name: item.title,
          acceptedAnswer: { "@type": "Answer", text: item.description },
        })),
      }),
    },
  ],
});

useSeoMeta({
  ogTitle: meta_tags.meta.find((m: any) => m.property === 'og:title')?.content || meta_tags.title,
  ogDescription: meta_tags.meta.find((m: any) => m.property === 'og:description')?.content,
  ogImage: meta_tags.meta.find((m: any) => m.property === 'og:image')?.content,
  ogUrl: meta_tags.canonical,
  ogType: 'website',
  twitterTitle: meta_tags.meta.find((m: any) => m.name === 'twitter:title')?.content,
  twitterDescription: meta_tags.meta.find((m: any) => m.name === 'twitter:description')?.content,
  twitterImage: meta_tags.meta.find((m: any) => m.name === 'twitter:image')?.content,
  twitterCard: 'summary_large_image',
  twitterSite: '@webreinvent',
  ogSiteName: 'WebReinvent',
});

definePageMeta({
  layout: "services-layout",
});

/* ─── Mapped Data ─── */

// ProcessCardsGrid: { title, description, svgIcon }
const quickHitCards = computed(() =>
  quickHits.items.map((card: any) => ({
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);

// ReadyToElevateYourBrand: { title, description, svgIcon }
const businessOutcomeCards = computed(() =>
  businessOutcomes.items.map((card: any) => ({
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);

// ServiceCardsSection: { svgIcon, title, desc }
const capabilitiesCards = computed(() =>
  capabilities.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    desc: card.desc,
  }))
);

// BenefitCards: { title, description, svgIcon }
const struggleItems = computed(() =>
  struggles.items.map((item: any) => ({
    title: item.title,
    description: item.description,
    svgIcon: item.svgIcon,
  }))
);

// IconLineDecorativeSquares: { number, title, description, svgIcon }
const techStackCards = computed(() =>
  techStack.items.map((card: any, i: number) => ({
    number: `0${i + 1}`,
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

// RealWorldApplications: { number, title, description }
const integrationCardsData = computed(() =>
  integrationCards.items.map((card: any, i: number) => ({
    number: `0${i + 1}`,
    title: card.title,
    description: card.description,
  }))
);

// DualCards: groupOne/groupTwo with { title, description, svgIcon }
const dualGroupOneCards = computed(() =>
  dualCards.groupOneCards.map((card: any) => ({
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);
const dualGroupTwoCards = computed(() =>
  dualCards.groupTwoCards.map((card: any) => ({
    title: card.title,
    description: card.description,
    svgIcon: card.svgIcon,
  }))
);

// FourCardTransformGrid: { svgIcon, title, description }
const whyChooseUsCards = computed(() =>
  whyChooseUs.items.map((card: any) => ({
    svgIcon: card.svgIcon,
    title: card.title,
    description: card.description,
  }))
);

const faqItems = computed(() => faq.faqItems);

const heroAlt = computed(() => String(hero.heading).replace(/<[^>]*>/g, '').trim());
</script>

<template>
  <main class="md:min-h-screen px-4 lg:px-0">
    <div class="max-w-screen-xl mx-auto">

      <!-- Section 1: Hero Card -->
      <header class="pt-32">
        <WrDesignHeroCard
          :heading="hero.heading"
          :subcopy="hero.subcopy"
          :subcopy-class="hero.subcopyClass"
          :cta-text="hero.ctaText"
          :image-src="hero.imageSrc"
          :alt="heroAlt"
          @cta-clicked="openModal"
        />
      </header>

      <!-- Section 1.5: Shine Text -->
      <section class="my-8" aria-label="Core Value Statement">
        <WrDesignShineText :text="shineText" />
      </section>

      <!-- Section 2: Benefits of Ghost (3-col grid with icons) -->
      <section aria-label="Benefits of Ghost Development">
        <WrDesignProcessCardsGrid
          :heading="quickHits.heading"
          :subcopy="quickHits.subtitle"
          :cards="quickHitCards"
          :cta-text="quickHits.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 3: Business Outcomes (horizontal row cards with icons) -->
      <section aria-label="Business Outcomes">
        <WrDesignReadyToElevateYourBrand
          :heading="businessOutcomes.heading"
          :subcopy="businessOutcomes.subcopy"
          :cards="businessOutcomeCards"
          :cta-text="businessOutcomes.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 4: Problem / Solution -->
      <section aria-labelledby="why-it-matters-heading">
        <WrProblemSolutionSection
          id="why-it-matters-heading"
          :heading="whyItMatters.heading"
          :intro="whyItMatters.subcopy"
          :problem-label="whyItMatters.lossLabel"
          :problems="whyItMatters.losses"
          :solution-label="whyItMatters.deliverLabel"
          :solutions="whyItMatters.delivers"
          :quote="whyItMatters.quote"
          :cta-text="whyItMatters.ctaText"
          :bg-image-light="whyItMatters.bgImageLight"
          :bg-image-dark="whyItMatters.bgImageDark"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 5: Capabilities (3-col grid with left border) -->
      <section aria-label="Ghost Development Capabilities">
        <WrDesignServiceCardsSection
          :headingLine1="capabilities.headingLine1"
          :headingLine2="capabilities.headingLine2"
          :subcopy="capabilities.subcopy"
          :cards="capabilitiesCards"
          :cta-text="capabilities.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 6: Struggles Ghost Solves (benefit cards with icons) -->
      <section aria-label="Struggles Ghost Solves">
        <WrDesignBenefitCards
          :heading="struggles.heading"
          :subcopy="struggles.subcopy"
          :cards="struggleItems"
        />
      </section>

      <!-- Section 7: Tech Stack (vertical list with decorative lines) -->
      <section aria-label="Technology Architecture">
        <WrDesignIconLineDecorativeSquaresCards
          :heading="techStack.heading"
          :subcopy="techStack.subcopy"
          :cards="techStackCards"
          :cta-text="techStack.ctaText"
          @cta-clicked="openModal"
        >
          <template v-for="(card, idx) in techStackCards" :key="card.title" #[`icon-${idx}`]>
            <WrIcon :name="card.svgIcon" class="size-6 sm:size-8 text-[#501D71] dark:text-[#CDA0FE]" />
          </template>
        </WrDesignIconLineDecorativeSquaresCards>
      </section>

      <!-- Section 8: Ghost vs Traditional CMS (dual-group cards) -->
      <section aria-label="Ghost vs Traditional CMS">
        <WrDesignDualCardsSection
          :heading="dualCards.heading"
          :subtitle="dualCards.subtitle"
          :group-one-title="dualCards.groupOneTitle"
          :group-one-cards="dualGroupOneCards"
          :group-two-title="dualCards.groupTwoTitle"
          :group-two-cards="dualGroupTwoCards"
          :cta-text="dualCards.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 9: Integrations (4-column numbered grid) -->
      <section aria-label="Integrations and Systems">
        <WrDesignRealWorldApplicationsSection
          :single-line-heading="integrationCards.heading"
          :subtitle="integrationCards.subcopy"
          :cards="integrationCardsData"
          :cta-text="integrationCards.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 10: Why Choose WebReinvent (2-col grid with crosshairs) -->
      <section aria-label="Why Choose WebReinvent">
        <WrDesignFourCardTransformGridSection
          :heading="whyChooseUs.heading"
          :subtitle="whyChooseUs.subcopy"
          :cards="whyChooseUsCards"
          :cta-text="whyChooseUs.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 11: CTA Block (quote + CTA) -->
      <section aria-label="Call to Action">
        <WrDesignQuoteCtaSection
          :text="cta.quote"
          :cta-text="cta.ctaText"
          @cta-clicked="openModal"
        />
      </section>

      <!-- Section 12: Stats -->
      <section class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]" aria-label="Company Statistics">
        <WrDesignStatsCard />
      </section>

      <!-- Section 13: FAQ -->
      <section aria-label="Frequently asked questions">
        <WrDesignFaqsSection
          :heading="faq.heading"
          :suffix="faq.suffix"
          :subtext="faq.subtext"
          :filter-tags="faq.filterTags"
          :faq-items="faqItems"
          :cta-text="faq.ctaText"
          @cta-clicked="openModal"
        />
      </section>
    </div>
  </main>

  <WrContactFormPopup :isOpen="visible" class="z-30" @close="visible = false" />
</template>

<style scoped>
:deep(.hero-image-wrapper) {
  max-width: 500px;
}
</style>
